<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
const props = defineProps({
  actionOptions: { type: Array, default: () => [] },
  canCounterOffer: { type: Boolean, default: false },
  form: { type: Object, required: true },
  saving: { type: Boolean, default: false },
  transitionOptions: { type: Array, default: () => [] },
});

const emit = defineEmits(['run', 'updateField']);

const hasAction = action =>
  props.actionOptions.some(option => option.name === action);

const updateField = (key, event) =>
  emit('updateField', { key, value: event.target.value });

const actionDisabled = action => {
  if (props.saving) return true;
  if (action === 'counter') return Number(props.form.counter_offer_amount) <= 0;
  if (action === 'transition') return !props.form.status;
  if (action === 'reschedule') return !props.form.rescheduled_at;
  if (action === 'finalizeOutcome') return !props.form.outcome;
  if (action === 'assign') return !props.form.responsible_user_id;
  if (action === 'cancel') {
    return !props.form.cancelled_at || !props.form.reason;
  }
  if (action === 'confirmPayment') {
    return !props.form.payment_responsibility_id;
  }
  return false;
};
</script>

<template>
  <article
    class="rounded-2xl border border-[#e8e4ef] bg-white p-5 shadow-[0_6px_18px_rgba(51,32,95,0.03)]"
  >
    <header class="flex items-center justify-between">
      <span>
        <h2 class="text-[13px] font-bold">Ações da operação</h2>
        <small class="mt-1 block text-[8px] text-[#69759c]">
          Campos e permissões retornados pelo backend.
        </small>
      </span>
      <span
        class="rounded-full bg-[#f3efff] px-2 py-1 text-[8px] font-bold text-[#5a24c7]"
      >
        API real
      </span>
    </header>

    <div
      v-if="canCounterOffer"
      class="mt-4 rounded-xl border border-[#e4d8fb] bg-[#fcfbff] p-3"
    >
      <strong class="text-[9px] text-[#5a24c7]">Contraproposta</strong>
      <div class="mt-3 grid grid-cols-2 gap-2">
        <label class="text-[8px] font-semibold">
          Novo valor
          <input
            :value="form.counter_offer_amount"
            class="mt-1.5 h-8 w-full rounded-lg border border-[#e5e0ec] px-2 text-[9px] font-normal outline-none focus:border-[#805ce1]"
            min="0"
            type="number"
            @input="updateField('counter_offer_amount', $event)"
          />
        </label>
        <label class="text-[8px] font-semibold">
          Validade
          <input
            :value="form.counter_expires_at"
            class="mt-1.5 h-8 w-full rounded-lg border border-[#e5e0ec] px-2 text-[9px] font-normal outline-none focus:border-[#805ce1]"
            type="date"
            @input="updateField('counter_expires_at', $event)"
          />
        </label>
      </div>
      <label class="mt-2 block text-[8px] font-semibold">
        Garantia
        <textarea
          :value="form.counter_guarantee"
          class="mt-1.5 min-h-[48px] w-full rounded-lg border border-[#e5e0ec] px-2 py-1.5 text-[9px] font-normal outline-none focus:border-[#805ce1]"
          @input="updateField('counter_guarantee', $event)"
        />
      </label>
    </div>

    <div
      v-if="hasAction('transition')"
      class="mt-4 rounded-xl border border-[#e4d8fb] bg-[#fcfbff] p-3"
    >
      <label class="block text-[8px] font-semibold">
        Novo status
        <select
          v-if="transitionOptions.length"
          :value="form.status"
          class="mt-1.5 h-8 w-full rounded-lg border border-[#e5e0ec] bg-white px-2 text-[9px] font-normal outline-none focus:border-[#805ce1]"
          @change="updateField('status', $event)"
        >
          <option value="">Selecione o status</option>
          <option
            v-for="option in transitionOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
        <input
          v-else
          :value="form.status"
          class="mt-1.5 h-8 w-full rounded-lg border border-[#e5e0ec] px-2 text-[9px] font-normal outline-none focus:border-[#805ce1]"
          placeholder="Status retornado pela API"
          type="text"
          @input="updateField('status', $event)"
        />
      </label>
    </div>

    <div
      v-if="hasAction('reschedule')"
      class="mt-4 rounded-xl border border-[#e4d8fb] bg-[#fcfbff] p-3"
    >
      <label class="block text-[8px] font-semibold">
        Novo horário da visita
        <input
          :value="form.rescheduled_at"
          class="mt-1.5 h-8 w-full rounded-lg border border-[#e5e0ec] px-2 text-[9px] font-normal outline-none focus:border-[#805ce1]"
          type="datetime-local"
          @input="updateField('rescheduled_at', $event)"
        />
      </label>
    </div>

    <div
      v-if="hasAction('finalizeOutcome')"
      class="mt-4 rounded-xl border border-[#e4d8fb] bg-[#fcfbff] p-3"
    >
      <div class="grid grid-cols-2 gap-2">
        <label class="text-[8px] font-semibold">
          Resultado
          <input
            :value="form.outcome"
            class="mt-1.5 h-8 w-full rounded-lg border border-[#e5e0ec] px-2 text-[9px] font-normal outline-none focus:border-[#805ce1]"
            placeholder="Resultado aceito pela API"
            type="text"
            @input="updateField('outcome', $event)"
          />
        </label>
        <label class="text-[8px] font-semibold">
          Próxima ação
          <input
            :value="form.next_action"
            class="mt-1.5 h-8 w-full rounded-lg border border-[#e5e0ec] px-2 text-[9px] font-normal outline-none focus:border-[#805ce1]"
            type="text"
            @input="updateField('next_action', $event)"
          />
        </label>
      </div>
    </div>

    <div
      v-if="hasAction('assign')"
      class="mt-4 rounded-xl border border-[#e4d8fb] bg-[#fcfbff] p-3"
    >
      <label class="block text-[8px] font-semibold">
        ID do responsável
        <input
          :value="form.responsible_user_id"
          class="mt-1.5 h-8 w-full rounded-lg border border-[#e5e0ec] px-2 text-[9px] font-normal outline-none focus:border-[#805ce1]"
          min="1"
          type="number"
          @input="updateField('responsible_user_id', $event)"
        />
      </label>
    </div>

    <div
      v-if="hasAction('cancel')"
      class="mt-4 rounded-xl border border-rose-100 bg-rose-50/40 p-3"
    >
      <label class="block text-[8px] font-semibold">
        Data do cancelamento
        <input
          :value="form.cancelled_at"
          class="mt-1.5 h-8 w-full rounded-lg border border-rose-100 bg-white px-2 text-[9px] font-normal outline-none focus:border-rose-300"
          type="date"
          @input="updateField('cancelled_at', $event)"
        />
      </label>
    </div>

    <div
      v-if="hasAction('confirmPayment')"
      class="mt-4 rounded-xl border border-[#e4d8fb] bg-[#fcfbff] p-3"
    >
      <label class="block text-[8px] font-semibold">
        ID da responsabilidade de pagamento
        <input
          :value="form.payment_responsibility_id"
          class="mt-1.5 h-8 w-full rounded-lg border border-[#e5e0ec] px-2 text-[9px] font-normal outline-none focus:border-[#805ce1]"
          min="1"
          type="number"
          @input="updateField('payment_responsibility_id', $event)"
        />
      </label>
    </div>

    <label
      v-if="actionOptions.length"
      class="mt-4 block text-[8px] font-semibold"
    >
      Motivo ou observação
      <textarea
        :value="form.reason"
        class="mt-1.5 min-h-[64px] w-full rounded-lg border border-[#e5e0ec] px-2 py-1.5 text-[9px] font-normal outline-none focus:border-[#805ce1]"
        @input="updateField('reason', $event)"
      />
    </label>

    <div v-if="actionOptions.length" class="mt-4 flex flex-wrap gap-2">
      <button
        v-for="action in actionOptions"
        :key="action.name"
        class="rounded-xl border border-[#d9cff3] bg-white px-4 py-2.5 text-[9px] font-bold text-[#5a24c7] disabled:cursor-not-allowed disabled:opacity-50"
        type="button"
        :disabled="actionDisabled(action.name)"
        @click="emit('run', action.name)"
      >
        {{ saving ? 'Processando…' : action.label }}
      </button>
    </div>
    <p
      v-else
      class="mt-4 rounded-xl bg-[#fbfaff] px-4 py-3 text-[9px] text-[#69759c]"
    >
      Nenhuma ação está permitida pelo backend para o status atual.
    </p>
  </article>
</template>
